import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { ErrorNotice, InfoNotice } from '../ui/States';
import { EventDetail, EventView, FORMAT_LABEL, KIND_LABEL, eventDates, torontoToday } from '../../lib/events';
import { errorMessage, inputClass } from '../../lib/programs';
import { AttendanceNumbers } from './CountsEditor';

/** A labelled field of the outreach dialog: label above (15/600 ink), then the control. */
function Field({
  id,
  title,
  wide = false,
  children,
}: {
  id: string;
  title: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-[6px] min-w-0 ${wide ? 'md:col-span-2' : ''}`}>
      <label htmlFor={id} className="text-[15px] leading-5 font-semibold text-ink">
        {title}
      </label>
      {children}
    </div>
  );
}

/** Log or edit an outreach talk or visit: where, when (a day or just the month), format, audience. */
export function OutreachForm({
  event,
  onClose,
  onSaved,
}: {
  event: EventView | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const o = event?.outreach ?? {};
  const first = event?.days[0];
  const [title, setTitle] = useState(event?.title ?? '');
  const [institution, setInstitution] = useState(o.institution ?? '');
  const [city, setCity] = useState(event?.city ?? '');
  const [format, setFormat] = useState<string>(o.format ?? 'in_person');
  const [monthOnly, setMonthOnly] = useState(o.datePrecision === 'month');
  const [date, setDate] = useState(first?.date ?? torontoToday());
  const [startTime, setStartTime] = useState(first?.startTime ?? '12:00');
  const [endTime, setEndTime] = useState(first?.endTime ?? '13:30');
  const [topic, setTopic] = useState(o.topic ?? '');
  const [audience, setAudience] = useState(o.audience ?? '');
  const save = useMutation(
    async () => {
      const day = monthOnly ? `${date.slice(0, 7)}-01` : date;
      const days =
        event && event.days.length > 1 && !monthOnly && day === first?.date
          ? event.days.map((d) => ({ date: d.date, startTime: d.startTime, endTime: d.endTime }))
          : [{ date: day, startTime, endTime }];
      const body = {
        title,
        kind: 'outreach',
        city: city || null,
        days,
        outreach: {
          institution: institution || null,
          format,
          topic: topic || null,
          audience: audience || null,
          datePrecision: monthOnly ? 'month' : null,
        },
      };
      const res = event ? await api.patch(`/events/${event.id}`, body) : await api.post('/events', body);
      return res.data.data as EventDetail;
    },
    { onSuccess: (d) => onSaved(d.event.id) },
  );
  const missing = [!title ? 'what it was' : null, !date ? 'a date' : null].filter(Boolean);
  return (
    <Modal
      title={event ? 'Edit the outreach talk or visit' : 'Log an outreach talk or visit'}
      why={
        event
          ? 'Numbers only: how many trainees, faculty and others came is recorded on the talk’s page. No names are kept for outreach.'
          : 'Numbers only: you record how many trainees, faculty and others came on the next page. No names are kept for outreach.'
      }
      onClose={onClose}
      width={680}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading || missing.length > 0} onClick={() => save.mutate()}>
            {save.isLoading ? 'Saving…' : event ? 'Save the changes' : 'Save and add numbers'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {missing.length ? (
            <p className="basis-full text-small text-ink-3">
              Saving turns on once there&rsquo;s {missing.join(' and ')}.
            </p>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <Field id="of-title" title="What it was" wide>
          <input
            id="of-title"
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="For example, lunch ’n’ learn at …"
          />
        </Field>
        <Field id="of-institution" title="Host institution or organization">
          <input
            id="of-institution"
            className={inputClass}
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
          />
        </Field>
        <Field id="of-city" title="City or place">
          <input id="of-city" className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field id="of-format" title="Format">
          <select id="of-format" className={inputClass} value={format} onChange={(e) => setFormat(e.target.value)}>
            {Object.entries(FORMAT_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field id="of-date" title={monthOnly ? 'Month' : 'Date'}>
          {monthOnly ? (
            <input
              id="of-date"
              type="month"
              className={inputClass}
              value={date.slice(0, 7)}
              onChange={(e) => setDate(`${e.target.value}-01`)}
            />
          ) : (
            <input
              id="of-date"
              type="date"
              className={inputClass}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </Field>
        <label className="md:col-span-2 flex items-center gap-[10px] min-h-[44px] text-[15px] leading-[22px] text-ink">
          <input
            type="checkbox"
            className="w-[18px] h-[18px] accent-accent"
            checked={monthOnly}
            onChange={(e) => setMonthOnly(e.target.checked)}
          />
          Only the month is known
        </label>
        {!monthOnly ? (
          <>
            <Field id="of-from" title="From">
              <input
                id="of-from"
                type="time"
                className={inputClass}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Field>
            <Field id="of-to" title="To">
              <input
                id="of-to"
                type="time"
                className={inputClass}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </Field>
          </>
        ) : null}
        <Field id="of-topic" title="Topic">
          <input id="of-topic" className={inputClass} value={topic} onChange={(e) => setTopic(e.target.value)} />
        </Field>
        <Field id="of-audience" title="Who it was for">
          <input
            id="of-audience"
            className={inputClass}
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="Trainees and faculty in psychology"
          />
        </Field>
      </div>
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

export function NotesEditor({ event, canEdit }: { event: EventView; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(event.notes ?? '');
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!box.current) return;
    box.current.style.height = 'auto';
    box.current.style.height = `${box.current.scrollHeight}px`;
  }, [text]);
  const save = useMutation(async () => api.patch(`/events/${event.id}`, { notes: text || null }), {
    onSuccess: () => queryClient.invalidateQueries('event'),
  });
  if (!canEdit)
    return event.notes ? (
      <p className="text-table whitespace-pre-wrap">{event.notes}</p>
    ) : (
      <p className="text-table text-ink-2">No notes.</p>
    );
  return (
    <div className="text-table">
      <textarea
        className={`${inputClass} w-full`}
        rows={3}
        ref={box}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="How it went, follow-ups, contacts to keep (no personal details of attendees)"
      />
      <Button
        size="sm"
        className="mt-2"
        disabled={save.isLoading || text === (event.notes ?? '')}
        onClick={() => save.mutate()}
      >
        {save.isLoading ? 'Saving…' : 'Save the notes'}
      </Button>
      {save.error ? (
        <div className="mt-2">
          <ErrorNotice>{errorMessage(save.error, 'Could not save the notes.')}</ErrorNotice>
        </div>
      ) : null}
    </div>
  );
}

/** An outreach talk or visit: details, attendance as numbers, notes. */
export default function OutreachPage({
  detail,
  mutable,
  refresh,
}: {
  detail: EventDetail;
  mutable: boolean;
  refresh: () => void;
}) {
  const e = detail.event;
  const o = e.outreach ?? {};
  const [edit, setEdit] = useState(false);
  const past = (e.days[e.days.length - 1]?.date ?? '') < torontoToday();
  return (
    <>
      <PageHeader
        eyebrow={
          <Link className="text-small underline" to="/events">
            All events
          </Link>
        }
        title={e.title}
        sub={[KIND_LABEL.outreach, eventDates(e), o.institution, e.city, o.format ? FORMAT_LABEL[o.format] : null]
          .filter(Boolean)
          .join(' · ')}
        aside={mutable ? <Button onClick={() => setEdit(true)}>Edit</Button> : undefined}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <AttendanceNumbers eventId={e.id} counts={e.counts} canEdit={mutable} past={past} />
        <Panel>
          <PanelHeader title="Details" />
          <PanelBody className="text-table">
            <dl className="grid grid-cols-[110px_1fr] gap-y-2">
              <dt className="text-ink-2">Host</dt>
              <dd>{o.institution || '—'}</dd>
              <dt className="text-ink-2">Place</dt>
              <dd>{e.city || '—'}</dd>
              <dt className="text-ink-2">Format</dt>
              <dd>{o.format ? FORMAT_LABEL[o.format] : '—'}</dd>
              <dt className="text-ink-2">When</dt>
              <dd>
                {eventDates(e)}
                {o.datePrecision === 'month' ? ' (day not recorded)' : ''}
              </dd>
              <dt className="text-ink-2">Topic</dt>
              <dd>{o.topic || '—'}</dd>
              <dt className="text-ink-2">For</dt>
              <dd>{o.audience || '—'}</dd>
            </dl>
          </PanelBody>
        </Panel>
        <Panel className="lg:col-span-3">
          <PanelHeader title="Notes" />
          <PanelBody>
            <NotesEditor event={e} canEdit={mutable} />
          </PanelBody>
        </Panel>
      </div>
      {e.counts ? (
        <div className="mt-5">
          <InfoNotice>
            Outreach counts are part of the attendance totals in the insights, but not of returning attendees or
            retention, because they have no names.
          </InfoNotice>
        </div>
      ) : null}
      {edit ? (
        <OutreachForm
          event={e}
          onClose={() => setEdit(false)}
          onSaved={() => {
            setEdit(false);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}
